import { FC, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as DriveIcon } from "../../../assets/drive.icon.svg";
import { ReactComponent as PlusCircleIcon } from "../../../assets/plus-circle.icon.svg";
import { ReactComponent as MoonIcon } from "../../../assets/moon.icon.svg";
import { ReactComponent as CubeIcon } from "../../../assets/cube.icon.svg";
import { ReactComponent as WarningIcon } from "../../../assets/warning.icon.svg";
import { ReactComponent as CheckCircleIcon } from "../../../assets/check-circle.icon.svg";
import { fetchQuestionUsage, QuestionUsage } from "../../../api/licensing/endpoints-referrers";
import { Logo } from "../../../common/enums/logo.enum";
import { UserRole } from "../../../common/enums/user-role.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { ThemeContainer } from "../../../common/containers/theme/theme.container";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import FeedbackView from "../feedback/feedback";
import LinkGoogleDriveView from "../link-google-drive/link-google-drive";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import {
    KnowledgeBaseMenu,
    KnowledgeBaseMenuItem,
    KnowledgeBaseMenuItemType
} from "../../../components/knowledge-base-menu/knowledge-base-menu.comp";
import { getAssistantIcon } from "../../../common/utilities/logo/logo.util";
import { visibleAdminNav } from "../../../common/utilities/routes/admin-nav.const";

interface DashboardItem {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    link?: string;
    action?: () => void;
    className?: string;
}

// Enter/Space activation for the action-only dashboard button, which renders as
// an <a> without href (not natively focusable or operable). role="button" +
// tabIndex + this handler make it keyboard-operable (WCAG 2.1.1 / 4.1.2).
const clickOnKey = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
    }
};

const DashboardButton = ({
    icon,
    title,
    link,
    disabled = false,
    onClick,
    className
}: {
    icon: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    link?: string;
    disabled?: boolean;
    onClick?: () => void;
    className?: string;
}) => {
    return link ? (
        <Link
            className={`dashboard-button${disabled ? " dashboard-button--disabled" : ""} ${className ?? ""}`}
            to={link}
            onClick={!disabled ? onClick : undefined}
        >
            <SvgIcon className="dashboard-button__icon" component={icon} inheritViewBox />
            <span className="dashboard-button__text">{title}</span>
        </Link>
    ) : (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid -- hrefless dashboard action exposed as role="button" with full keyboard support (Enter/Space via clickOnKey); kept as <a> to share the a.dashboard-button styling that also covers the <Link> branch above.
        <a
            className={`dashboard-button${disabled ? " dashboard-button--disabled" : ""} ${className ?? ""}`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onClick={!disabled ? onClick : undefined}
            onKeyDown={!disabled ? clickOnKey : undefined}
        >
            <SvgIcon className="dashboard-button__icon" component={icon} inheritViewBox />
            <span className="dashboard-button__text">{title}</span>
        </a>
    );
};

const DashboardView = () => {
    const providerPathname = "knowledge-base";
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const { logo, isLightTheme, changeTheme, setLoadingLoader } = ThemeContainer.useContainer();
    const {
        isTrial,
        isSuperAdmin,
        isAdmin,
        user,
        knowledgeBase,
        knowledgeBases,
        hasAdminFeature,
        updateUserKnowledgeBase
    } = FirebaseAuthContainer.useContainer();
    const [providerItems, setProviderItems] = useState<DashboardItem[]>([]);
    const [kbList, setKBList] = useState<KnowledgeBaseMenuItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [upgradeScreenOpen, setUpgradeScreenOpen] = useState<boolean>(false);
    const [addSourceModalOpen, setAddSourceModalOpen] = useState<boolean>(false);
    const [questionUsage, setQuestionUsage] = useState<QuestionUsage | null>(null);

    const canSeeUsage = isSuperAdmin || user?.role === UserRole.admin;

    useEffect(() => {
        if (!canSeeUsage) return;
        fetchQuestionUsage()
            .then(res => setQuestionUsage(res.data))
            .catch(() => setQuestionUsage(null));
    }, [canSeeUsage]);

    const handleUpgradeScreenOpen = () => {
        setUpgradeScreenOpen(true);
    };

    const handleUpgradeScreenClose = () => {
        setUpgradeScreenOpen(false);
    };

    const handleAddSourceModalOpen = () => {
        setAddSourceModalOpen(true);
    };

    const handleAddSourceModalClose = () => {
        setAddSourceModalOpen(false);
    };

    const handleAddSourceSuccess = () => {
        setIsLoading(true);
        loadProviders();
    };

    const assistantAvailable =
        !!user?.currentKnowledgeBaseId &&
        knowledgeBases.find(kb => kb.uid === user.currentKnowledgeBaseId)?.status === "active";

    const isTrialExpired =
        isTrial &&
        knowledgeBase?.expiringTrialPeriod != null &&
        knowledgeBase.expiringTrialPeriod < 1;

    const chatDisabled = !assistantAvailable || isTrialExpired;

    const handleChangeTheme = () => {
        changeTheme();
    };

    useEffect(() => {
        setProviderItems(
            providers
                .sort((a, b) => a.id - b.id)
                .map(provider => ({
                    icon: DriveIcon,
                    title:
                        provider.name === ProviderType.fileUploader
                            ? "My Files"
                            : provider.displayName,
                    link: `/${providerPathname}/${provider.id}`
                }))
        );
        setIsLoading(false);
    }, [providers]);

    useEffect(() => {
        setKBList(
            knowledgeBases.map(kb => ({
                type: KnowledgeBaseMenuItemType.stroke,
                icon: CubeIcon,
                title: kb.displayName,
                role: kb.role,
                kbId: kb.uid,
                logoUrl: kb.logoUrl,
                action: () => {
                    setLoadingLoader(true);
                    updateUserKnowledgeBase(kb.uid).then(() => {
                        if (kb.role === "user") {
                            window.location.href = "/new-chat";
                        }
                        setLoadingLoader(false);
                    });
                }
            }))
        );
    }, [knowledgeBases, updateUserKnowledgeBase, setLoadingLoader]);

    const handleAssistantAction = () => {
        window.location.href = "/new-chat";
    };

    return (
        <HeaderLayout>
            <div className="dashboard-view">
                <div className={`dashboard-body${!isAdmin ? " dashboard-body--user" : ""}`}>
                    <div className="dashboard-body-header">
                        {knowledgeBase?.logoUrl && (
                            <img className="dashboard-logo" src={knowledgeBase.logoUrl} alt="" />
                        )}
                        <div className="dashboard-info">
                            <span className="dashboard-title" role="heading" aria-level={1}>
                                {knowledgeBase?.displayName ?? ""}
                            </span>
                            {knowledgeBase?.description && (
                                <span className="dashboard-description">
                                    {knowledgeBase.description}
                                </span>
                            )}
                        </div>
                    </div>
                    {isAdmin && (
                        <div className="dashboard-content">
                            {!isTrial && (
                                <div className="dashboard-container">
                                    <div className="dashboard-bubble-block">
                                        <div
                                            className="dashboard-bubble-title"
                                            role="heading"
                                            aria-level={2}
                                        >
                                            Connected sources
                                        </div>
                                        <div className="dashboard-bubble-separator"></div>
                                        {providerItems.map(item => (
                                            <DashboardButton
                                                key={crypto.randomUUID()}
                                                icon={item.icon}
                                                title={item.title}
                                                link={item.link}
                                                className="dashboard-button--margin"
                                            />
                                        ))}
                                        <DashboardButton
                                            icon={PlusCircleIcon}
                                            title="Add Source"
                                            onClick={handleAddSourceModalOpen}
                                            className="dashboard-button--margin"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="dashboard-container">
                                <div className="dashboard-bubble-block">
                                    <div
                                        className="dashboard-bubble-title"
                                        role="heading"
                                        aria-level={2}
                                    >
                                        Settings
                                    </div>
                                    <div className="dashboard-bubble-separator"></div>
                                    {/* Phase C: tiles derive from ADMIN_NAV (shared with the
                                        gear menu and page titles). Per-Assistant settings
                                        (prompt, retrieval, embedding, customization, chat
                                        dashboard, invites) live in the Assistants dialog;
                                        Delete Assistant is a row action there. */}
                                    {visibleAdminNav({
                                        canManage: !!isAdmin,
                                        isCompanyAdmin:
                                            !!isSuperAdmin || user?.role === UserRole.admin,
                                        isSuperAdmin: !!isSuperAdmin,
                                        hasAdminFeature
                                    }).map(entry => (
                                        <DashboardButton
                                            key={entry.path}
                                            icon={entry.icon}
                                            title={entry.title}
                                            link={entry.path}
                                            className="dashboard-button--margin"
                                        />
                                    ))}
                                    <DashboardButton
                                        icon={MoonIcon}
                                        title={isLightTheme ? "Dark theme" : "Light theme"}
                                        onClick={handleChangeTheme}
                                        className="dashboard-button--margin"
                                    />
                                </div>
                                {canSeeUsage && questionUsage?.enabled && (
                                    <div className="dashboard-bubble-block">
                                        <div
                                            className="dashboard-bubble-title"
                                            role="heading"
                                            aria-level={2}
                                        >
                                            Questions Usage
                                        </div>
                                        <div className="dashboard-bubble-separator"></div>
                                        <div className="dashboard-usage">
                                            <div className="dashboard-usage__row">
                                                <span className="dashboard-usage__label">
                                                    Purchased
                                                </span>
                                                <span className="dashboard-usage__value">
                                                    {questionUsage.limit ?? "—"}
                                                </span>
                                            </div>
                                            <div className="dashboard-usage__row">
                                                <span className="dashboard-usage__label">Used</span>
                                                <span className="dashboard-usage__value">
                                                    {questionUsage.used}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="dashboard-bubble-block">
                                    <DashboardButton
                                        icon={getAssistantIcon(logo)}
                                        title="Chat"
                                        link="/new-chat"
                                        disabled={chatDisabled}
                                        className={
                                            logo === Logo.mba
                                                ? "dashboard-button--mba"
                                                : "dashboard-button--tiva"
                                        }
                                    />
                                </div>
                            </div>
                            {isTrial && (
                                <div className="dashboard-container">
                                    <div className="dashboard-bubble-block">
                                        <div className="dashboard-bubble-title dashboard-bubble-title--warning">
                                            <SvgIcon
                                                className="dashboard-bubble-title__icon"
                                                component={WarningIcon}
                                                inheritViewBox
                                            />
                                            <span className="dashboard-bubble-title__text">
                                                {`Trial expires in ${knowledgeBase?.expiringTrialPeriod} days`}
                                            </span>
                                        </div>
                                        <Button
                                            className="trial-button dashboard-button--trial"
                                            variant="text"
                                            onClick={handleUpgradeScreenOpen}
                                        >
                                            Upgrade
                                        </Button>
                                        <div className="dashboard-bubble-trial-info">
                                            <span className="dashboard-bubble-trial-info-title">
                                                In full version you’ll get:
                                            </span>
                                            <div className="dashboard-bubble-trial-info-description">
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        Unlimited usage
                                                    </span>
                                                </div>
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        Unlimited Assistants
                                                    </span>
                                                </div>
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        Unlimited docs for upload
                                                    </span>
                                                </div>
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        External file sources
                                                    </span>
                                                </div>
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        Unlimited users
                                                    </span>
                                                </div>
                                                <div className="dashboard-bubble-trial-info-description-item">
                                                    <SvgIcon
                                                        className="dashboard-bubble-trial-info-description-item__icon"
                                                        component={CheckCircleIcon}
                                                        inheritViewBox
                                                    />
                                                    <span className="dashboard-bubble-trial-info-description-item__text">
                                                        Manager role
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {isAdmin === false && (
                        <div className="dashboard-user-content">
                            <div className="dashboard-user-content-description">
                                You don&apos;t have sufficient privileges to admin this Assistant.
                                Please switch to Assistant or choose another Assistant.
                            </div>
                            <Button
                                className="primary-button dashboard-user-content-action"
                                variant="outlined"
                                onClick={handleAssistantAction}
                            >
                                Switch to Assistant
                            </Button>
                            {kbList.length > 1 && (
                                <KnowledgeBaseMenu
                                    className="dashboard-user-content-action"
                                    items={kbList}
                                    currentKnowledgeBaseId={user?.currentKnowledgeBaseId}
                                />
                            )}
                        </div>
                    )}
                </div>
                <LoaderView show={isLoading} />
            </div>
            <ModalTemplate
                open={upgradeScreenOpen}
                width={654}
                handleClose={handleUpgradeScreenClose}
                ariaLabel="Upgrade to full version"
            >
                <FeedbackView handleClose={handleUpgradeScreenClose} />
            </ModalTemplate>
            <ModalTemplate
                open={addSourceModalOpen}
                width={654}
                handleClose={handleAddSourceModalClose}
                ariaLabel="Add Source"
            >
                <LinkGoogleDriveView
                    handleClose={handleAddSourceModalClose}
                    handleSuccess={handleAddSourceSuccess}
                />
            </ModalTemplate>
        </HeaderLayout>
    );
};

export default DashboardView;
